import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { extractError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldAlert, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md bg-white rounded-xl border border-slate-200 shadow-sm p-8">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ background: "#023E73" }}><ShieldAlert size={18} /></div>
          <div className="font-display font-bold text-base">Warranty &amp; Risk Manager</div>
        </div>
        {children}
        <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-login">
          <ArrowLeft size={14} /> Retour à la connexion
        </Link>
      </div>
    </div>
  );
}

function ForgotForm() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(extractError(err, "Une erreur est survenue"));
    } finally { setSubmitting(false); }
  };

  return (
    <Shell>
      <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Mot de passe oublié</h2>
      {sent ? (
        <div className="mt-6 flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 px-3 py-3 rounded-md" data-testid="forgot-success">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <span>Si un compte existe pour <b>{email}</b>, un lien de réinitialisation vient d&apos;être envoyé. Pensez à vérifier vos spams — le lien est valable 1 heure.</span>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-500 mt-2 mb-6">Saisissez votre email — nous vous enverrons un lien pour définir un nouveau mot de passe.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</Label>
              <Input id="email" data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-11" />
            </div>
            <Button type="submit" data-testid="forgot-submit" disabled={submitting || !email} className="w-full h-11 text-white font-semibold" style={{ background: "#023E73" }}>
              {submitting ? "Envoi..." : "Envoyer le lien de réinitialisation"}
            </Button>
          </form>
        </>
      )}
    </Shell>
  );
}

function ResetForm({ token }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (password !== confirm) return setErr("Les deux mots de passe ne correspondent pas.");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Mot de passe réinitialisé — connectez-vous");
      navigate("/login");
    } catch (e2) {
      setErr(extractError(e2, "Lien invalide ou expiré"));
    } finally { setSubmitting(false); }
  };

  return (
    <Shell>
      <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Nouveau mot de passe</h2>
      <p className="text-sm text-slate-500 mt-2 mb-6">Minimum 12 caractères avec majuscule, minuscule, chiffre et caractère spécial.</p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="pw" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nouveau mot de passe</Label>
          <Input id="pw" data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 h-11" />
        </div>
        <div>
          <Label htmlFor="pw2" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Confirmer le mot de passe</Label>
          <Input id="pw2" data-testid="reset-password-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5 h-11" />
        </div>
        {err && (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-md" data-testid="reset-error">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{err}</span>
          </div>
        )}
        <Button type="submit" data-testid="reset-submit" disabled={submitting || !password || !confirm} className="w-full h-11 text-white font-semibold" style={{ background: "#023E73" }}>
          {submitting ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
        </Button>
      </form>
    </Shell>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return token ? <ResetForm token={token} /> : <ForgotForm />;
}
